import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { tokenManager } from '../contexts/AuthContext';
import { refreshAuthToken } from '../services/api';

const TokenRefreshTest = () => {
  const { isAuthenticated, user, token } = useAuth();
  const [testResults, setTestResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const addTestResult = (test, result, details = '') => {
    setTestResults(prev => [...prev, {
      timestamp: new Date().toLocaleTimeString(),
      test,
      result,
      details
    }]);
  };

  const clearResults = () => {
    setTestResults([]);
  };

  // Test 1: Check current token status
  const testCurrentTokenStatus = () => {
    const accessToken = tokenManager.getToken();
    const refreshToken = tokenManager.getRefreshToken();
    const isExpired = accessToken ? tokenManager.isTokenExpired(accessToken) : true;
    
    addTestResult(
      'Current Token Status',
      'INFO',
      `Access Token: ${accessToken ? 'Present' : 'Missing'}, Refresh Token: ${refreshToken ? 'Present' : 'Missing'}, Expired: ${isExpired}`
    );
  };

  // Test 2: Manually trigger token refresh
  const testManualRefresh = async () => {
    setLoading(true);
    try {
      const result = await refreshAuthToken();
      if (result.success) {
        addTestResult('Manual Refresh', 'SUCCESS', `New token received: ${result.token.substring(0, 20)}...`);
      } else {
        addTestResult('Manual Refresh', 'FAILED', result.error);
      }
    } catch (error) {
      addTestResult('Manual Refresh', 'ERROR', error.message);
    }
    setLoading(false);
  };

  // Test 3: Simulate expired access token (but keep refresh token)
  const testExpiredAccessToken = () => {
    const refreshToken = tokenManager.getRefreshToken();
    if (!refreshToken) {
      addTestResult('Simulate Expired Access Token', 'FAILED', 'No refresh token available');
      return;
    }

    // Remove only access token, keep refresh token
    localStorage.removeItem('auth_access_token');
    addTestResult('Simulate Expired Access Token', 'SUCCESS', 'Access token removed, refresh token preserved. Refresh page to test auto-refresh.');
  };

  // Test 4: Create a fake expired token
  const testFakeExpiredToken = () => {
    // Create a JWT-like token that's already expired
    const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
    const payload = btoa(JSON.stringify({ 
      user_id: 'test', 
      exp: Math.floor(Date.now() / 1000) - 3600 // Expired 1 hour ago
    }));
    const signature = 'fake-signature';
    const expiredToken = `${header}.${payload}.${signature}`;
    
    localStorage.setItem('auth_access_token', expiredToken);
    addTestResult('Create Fake Expired Token', 'SUCCESS', 'Fake expired token created. Make an API call to test auto-refresh.');
  };

  // Test 5: Test API call with current token
  const testApiCall = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/auth/me/', {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        addTestResult('API Call Test', 'SUCCESS', 'API call successful');
      } else {
        addTestResult('API Call Test', 'FAILED', `HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      addTestResult('API Call Test', 'ERROR', error.message);
    }
    setLoading(false);
  };

  if (!isAuthenticated) {
    return (
      <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
        <h2 className="text-lg font-semibold text-yellow-800 mb-2">Token Refresh Test</h2>
        <p className="text-yellow-700">Please login first to test token refresh functionality.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">JWT Token Refresh Test Suite</h2>
        
        {/* Current Status */}
        <div className="mb-6 p-4 bg-blue-50 rounded-lg">
          <h3 className="font-semibold text-blue-800 mb-2">Current Status</h3>
          <div className="text-sm text-blue-700 space-y-1">
            <div>User: {user?.display_name || 'Unknown'}</div>
            <div>Authenticated: {isAuthenticated ? 'Yes' : 'No'}</div>
            <div>Access Token: {token ? `${token.substring(0, 20)}...` : 'None'}</div>
            <div>Refresh Token: {tokenManager.getRefreshToken() ? 'Present' : 'Missing'}</div>
          </div>
        </div>

        {/* Test Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <button
            onClick={testCurrentTokenStatus}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Check Token Status
          </button>
          
          <button
            onClick={testManualRefresh}
            disabled={loading}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
          >
            Manual Refresh
          </button>
          
          <button
            onClick={testExpiredAccessToken}
            className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700"
          >
            Simulate Expired Access
          </button>
          
          <button
            onClick={testFakeExpiredToken}
            className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700"
          >
            Create Fake Expired Token
          </button>
          
          <button
            onClick={testApiCall}
            disabled={loading}
            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
          >
            Test API Call
          </button>
          
          <button
            onClick={clearResults}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Clear Results
          </button>
        </div>

        {/* Test Results */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-semibold text-gray-800 mb-3">Test Results</h3>
          {testResults.length === 0 ? (
            <p className="text-gray-500 text-sm">No tests run yet. Click a test button above.</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {testResults.map((result, index) => (
                <div
                  key={index}
                  className={`p-3 rounded text-sm ${
                    result.result === 'SUCCESS' ? 'bg-green-100 text-green-800' :
                    result.result === 'FAILED' ? 'bg-red-100 text-red-800' :
                    result.result === 'ERROR' ? 'bg-red-100 text-red-800' :
                    'bg-blue-100 text-blue-800'
                  }`}
                >
                  <div className="font-semibold">
                    [{result.timestamp}] {result.test}: {result.result}
                  </div>
                  {result.details && (
                    <div className="mt-1 text-xs opacity-80">{result.details}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="mt-6 p-4 bg-gray-100 rounded-lg">
          <h3 className="font-semibold text-gray-800 mb-2">Testing Instructions</h3>
          <ol className="text-sm text-gray-700 space-y-1 list-decimal list-inside">
            <li><strong>Check Token Status</strong>: View current token information</li>
            <li><strong>Manual Refresh</strong>: Manually trigger token refresh using refresh token</li>
            <li><strong>Simulate Expired Access</strong>: Remove access token but keep refresh token, then refresh page</li>
            <li><strong>Create Fake Expired Token</strong>: Create an expired token to test auto-refresh on API calls</li>
            <li><strong>Test API Call</strong>: Make an API call to trigger automatic token refresh if needed</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default TokenRefreshTest;